import { FlaskConical } from "lucide-react";
import { SectionTitle, Sub, RefLink, P, Pill, ConnectsTo } from "./GuideHelpers";
import { PageCard } from "./PageCard";

export const SectionBrandSignals = () => (
  <>
    <SectionTitle id="brand-signals" scope="brand">Brand Signals</SectionTitle>
    <P>
      <strong>Brand Signals are the durable knowledge layer of the platform.</strong> Where A/B Tests
      are individual experiments — each a single data point — Brand Signals are the patterns that
      emerge when many tests point in the same direction. They answer the question:{" "}
      <em>"What do we actually believe about how this brand wins on Amazon, and how strong is the evidence?"</em>
    </P>
    <P>
      A Brand Signal is never created by hand from a single test. It is <strong>aggregated</strong> from
      the hypothesis matrices of many completed tests, promoted through maturity tiers as evidence
      accumulates, and split or annotated when evidence diverges. The pipeline below describes exactly
      how that happens.
    </P>

    <Sub id="bs-model">Evidence model — the hypothesis matrix</Sub>
    <P>
      When a test is set up, the user states a primary hypothesis (e.g. <em>"Leading the title with
      'reapply over makeup' will lift CVR for tinted SPF"</em>). But the winning variant almost always
      embodies <strong>multiple changes at once</strong>: a new keyword, a structural reorder, a tone
      shift, added social proof. Attributing the win to a single cause is naive.
    </P>
    <P>
      Instead, every completed test carries a <strong>hypothesis matrix</strong> — a structured list
      of every candidate explanation for the outcome, tagged by which variant it lives in and what
      taxonomy bucket it belongs to. The taxonomy has 9 buckets:
    </P>
    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li><strong className="text-foreground">intent-shift</strong> — Changing the primary framing/intent of the copy.</li>
      <li><strong className="text-foreground">keyword-add</strong> — Adding a specific keyword or phrase.</li>
      <li><strong className="text-foreground">keyword-position</strong> — Moving a keyword to a more prominent slot.</li>
      <li><strong className="text-foreground">structure-change</strong> — Reordering or restructuring (e.g. lead with use-case).</li>
      <li><strong className="text-foreground">tone-shift</strong> — Emotional vs. functional language.</li>
      <li><strong className="text-foreground">length-change</strong> — Shorter or longer copy.</li>
      <li><strong className="text-foreground">format-change</strong> — Punctuation, separators, capitalization.</li>
      <li><strong className="text-foreground">social-proof</strong> — Adding "best-selling", "#1", reviews, awards.</li>
      <li><strong className="text-foreground">specificity-add</strong> — Adding a number, ingredient %, or measurable claim.</li>
    </ul>
    <P>
      Each matrix entry has a stable composite ID like <code>keyword-add:reapply-spf-over-makeup</code> —
      a type plus a specific instance. This dual identity is what enables the type-vs-instance hierarchy
      described below.
    </P>

    <P>
      <strong>Winner-only evidence.</strong> When a test concludes:
    </P>
    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li>If <strong>Variant B wins</strong>, every hypothesis in the matrix tagged <code>presentIn: "B"</code> contributes one supporting vote to its matching signal.</li>
      <li>If <strong>Variant A wins</strong> (the defender holds), Variant A's hypotheses contribute votes too — a defender win is just as informative as a challenger win.</li>
      <li>If the test is <strong>inconclusive</strong>, <strong>nothing</strong> is contributed. Inconclusive tests are noise, not evidence.</li>
    </ul>
    <P>
      This is implemented in <code>getWinningHypotheses(test)</code>. Losing-side hypotheses are
      discarded — they failed to drive the win, so they don't earn a vote, even if they were
      individually plausible.
    </P>

    <Sub id="bs-hierarchy">Type → Instance hierarchy</Sub>
    <P>
      Signals exist at <strong>two levels</strong>, and both accumulate evidence in parallel:
    </P>
    <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground mb-3">
      <li>
        <strong className="text-foreground">Type-level (parent):</strong> the abstract pattern, e.g.{" "}
        <em>"Adding specific reapplication-context keywords lifts CVR"</em> — taxonomy bucket{" "}
        <code>keyword-add</code>, no specific instance.
      </li>
      <li>
        <strong className="text-foreground">Instance-level (child):</strong> the specific value, e.g.{" "}
        <em>"keyword-add: reapply SPF over makeup"</em> — same bucket, a single concrete keyword.
      </li>
    </ul>
    <P>
      A vote always lands on its <strong>instance-level</strong> signal first, then rolls up to its
      <strong> type-level</strong> parent. So an instance signal might have 3 supporting tests while
      its parent (which absorbs every <code>keyword-add</code> instance under it) has 28. Type-level
      signals tell you <em>"keyword adds work for this brand."</em> Instance-level signals tell you{" "}
      <em>"this specific keyword is the proven winner."</em>
    </P>
    <P>
      Both type-level and instance-level signals appear as <strong>peer rows in a single flat
      table</strong> — instances are not nested under their type as expandable children. The
      relationship is preserved through the shared taxonomy tag (<code>keyword-add</code>) and the
      type filter, so you can pivot between "show me every instance under this type" and "show me
      just the rolled-up types" without a tree UI. This keeps the table scannable and avoids the
      depth-collapse problem when a type has dozens of instances.
    </P>

    <Sub id="bs-maturity">Maturity tiers — promotion via cross-context breadth</Sub>
    <P>
      A signal's maturity is <strong>not</strong> just a count of supporting tests. Counting alone
      would let a single chatty ASIN dominate. Maturity is gated on{" "}
      <strong>breadth of evidence across distinct contexts</strong> — multiple ASINs, multiple
      categories. Implemented in <code>deriveMaturity()</code>:
    </P>
    <div className="rounded-sm border border-border overflow-hidden mb-4">
      <table className="w-full text-sm">
        <thead className="bg-surface-tint-medium text-foreground">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Tier</th>
            <th className="text-left px-3 py-2 font-semibold">Threshold</th>
            <th className="text-left px-3 py-2 font-semibold">What it means</th>
          </tr>
        </thead>
        <tbody className="text-muted-foreground">
          <tr className="border-t border-border">
            <td className="px-3 py-2"><Pill variant="muted">Candidate</Pill></td>
            <td className="px-3 py-2">1 supporting test</td>
            <td className="px-3 py-2">Raw, noisy. Hidden from the default Brand Signals view (collapsed behind a count chip).</td>
          </tr>
          <tr className="border-t border-border">
            <td className="px-3 py-2"><Pill>Emerging</Pill></td>
            <td className="px-3 py-2">2+ tests</td>
            <td className="px-3 py-2">A pattern is starting. Worth watching, not yet worth acting on.</td>
          </tr>
          <tr className="border-t border-border">
            <td className="px-3 py-2"><Pill variant="green">Validated</Pill></td>
            <td className="px-3 py-2">3+ tests <strong>AND</strong> 2+ unique ASINs</td>
            <td className="px-3 py-2">Confirmed across products — actionable. The "use this" tier.</td>
          </tr>
          <tr className="border-t border-border">
            <td className="px-3 py-2"><Pill variant="green">Established</Pill></td>
            <td className="px-3 py-2">5+ tests <strong>AND</strong> 2+ categories</td>
            <td className="px-3 py-2">Broadly true at the brand level. These are the brand's "rules" and inform future Strategy priors.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <P>
      The <strong>AND</strong> conditions matter. Three supporting tests all on the same ASIN yields
      an Emerging signal, not Validated — because the evidence hasn't been stress-tested against a
      second context. This guards against over-fitting to a single product's quirks.
    </P>

    <Sub id="bs-splits">Context splits — when evidence disagrees with itself</Sub>
    <P>
      Sometimes a signal accumulates 8 supporting tests but the picture isn't uniform: it lifts
      CVR strongly for skincare ASINs, does nothing for haircare, and actively hurts setting-powder
      tests. Forcing this into a single "true / false" signal would be wrong.
    </P>
    <P>
      Instead, when evidence diverges along an axis (category, ASIN tier, intent type, etc.), the
      signal records <strong>context splits</strong>. Each split captures: the axis it splits on, the
      scope label, whether the signal holds in that scope, the test count and average CVR lift in
      that scope, and a one-line note explaining the divergence. Splits are stored on the signal
      record but only surfaced in the <strong>signal detail drawer</strong> — the parent row stays
      uncluttered.
    </P>
    <P>
      Practically, this lets a Validated signal honestly read:{" "}
      <em>"Validated for skincare and tinted SPF; refuted for setting powder; insufficient evidence
      for haircare."</em> That's a much more truthful artifact than a bare green checkmark.
    </P>
    <P>
      <strong>This is also how intra-brand category variation is handled.</strong> A brand like
      Supergoop! spans several product categories (mineral SPF, chemical SPF, body, lip). When a
      signal aggregated from that brand's tests behaves differently across those categories, we do
      not fork a new "Brand × Category" signal type — it stays one Brand Signal, and the{" "}
      <code>category</code> axis of its context splits carries the nuance. The Established tier
      (5+ tests AND 2+ categories) is what confirms a signal is genuinely brand-wide rather than
      category-bound; splits are what record the cases where it isn't.
    </P>

    <Sub id="bs-entanglement">Entanglement — silently tracked co-occurrence</Sub>
    <P>
      Because the hypothesis matrix tags every change in a winning variant, two hypotheses often
      ride together: <em>"social-proof: best-selling"</em> and <em>"specificity-add: SPF 50"</em> may
      co-occur in 7 of 10 winning variants. We can't fully untangle them without isolated tests,
      but we shouldn't pretend the entanglement isn't there either.
    </P>
    <P>
      Signal records entanglements silently — each signal carries a list of co-occurring signal IDs
      with co-occurrence count and rate. These never appear in the main table (they would be
      misleading without context), but the signal detail drawer surfaces them as:{" "}
      <em>"This signal often co-occurs with X (7 of 10 supporting tests). An isolated test of either
      change would strengthen attribution."</em> This is also where the drawer's "suggest a test to
      disentangle" CTA comes from.
    </P>

    <Sub id="bs-authorship">Authorship & lifecycle</Sub>
    <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground mb-3">
      <li>
        <strong className="text-foreground">Signal AI (default):</strong> The pipeline auto-creates and
        promotes signals as votes accumulate. Most rows you see are AI-authored.
      </li>
      <li>
        <strong className="text-foreground">Manual:</strong> A user can hand-create a signal to capture
        an institutional belief that hasn't yet shown up in tests. Manual signals start as Candidate
        and are promoted by the same pipeline once supporting tests appear.
      </li>
      <li>
        <strong className="text-foreground">Demotion:</strong> If new tests refute a signal — i.e.
        losing-side appearances start outweighing winning-side appearances at a sustained rate —
        the signal can be demoted a tier. Demotions surface as a notification on the Dashboard so
        the team isn't quietly relying on stale knowledge.
      </li>
      <li>
        <strong className="text-foreground">Lifecycle output:</strong> Established signals feed back
        into <RefLink to="seo-insights">Strategy</RefLink> as priors, biasing the AI's
        recommendations toward changes the brand has already proven.
      </li>
    </ul>

    <Sub id="bs-category">Category Signals — the Recom-internal layer</Sub>
    <P>
      Brand Signals stop at the brand boundary: a signal aggregated from Supergoop! tests is shown
      only to Supergoop! Category Signals do the same aggregation one level up — across{" "}
      <strong>multiple brands within a single category</strong> (e.g. all sunscreen brands) — and
      are <strong>Recom-internal only</strong>.
    </P>
    <P>
      Mechanically, Category Signals run the exact same pipeline (hypothesis matrix → winner-only
      votes → type/instance hierarchy → maturity tiers → context splits → entanglement). The only
      difference is the evidence pool: <code>scope: "category"</code> with a <code>categoryId</code> instead
      of <code>scope: "brand"</code> with a <code>brandId</code>. They live under{" "}
      <code>/analytics/category-signals</code>.
    </P>
    <div className="rounded-sm border border-border bg-status-warning-soft/40 p-3 mb-4">
      <p className="text-xs text-foreground">
        <strong>Hard rule:</strong> Category-scoped signals must <strong>never</strong> be surfaced in
        any brand-facing view. A brand user must not be able to infer that cross-brand aggregation
        is happening with their data. The <code>SignalScope</code> type and the brand-vs-cross-brand
        filtering on every signal query enforce this.
      </p>
    </div>
    <P>
      Why this matters: category signals are how Recom builds its own playbook of what works in
      each category, which informs onboarding for new brands ("when a new sunscreen brand joins,
      we already know SPF-in-first-3-words is established at the category level") and powers the{" "}
      <RefLink to="analytics-signal-roi">Signal ROI</RefLink> analytics on aggregate platform impact.
    </P>

    <PageCard icon={FlaskConical} title="Brand Signals page" route="/ab-tests" purpose="The brand-scoped signal table — a single page that combines the signal pool with the underlying A/B test list.">
      <Sub>Two tabs</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li><strong className="text-foreground">Brand Signals tab</strong> — The signal table. Type-level and instance-level signals appear as peer rows in a single flat table, linked by the shared taxonomy tag rather than nested as expandable children. Candidate-tier signals are hidden by default behind a count chip; Emerging and above are visible. Filter by Type, Maturity, Scope, and Source.</li>
        <li><strong className="text-foreground">A/B Tests tab</strong> — The aggregated test list across all products in the brand, with filtering by product, status, result, and scope. This is the underlying evidence pool.</li>
      </ul>
      <P>
        Clicking any signal row opens the <strong>signal detail drawer</strong> with the full hypothesis
        statement, maturity progression bar, supporting tests list, scope breakdown, context-split
        sub-signals, entanglement notes, and a "suggest a test to extend scope" CTA.
      </P>
      <ConnectsTo pages={["A/B Tests (product)", "Strategy", "Dashboard"]} />
    </PageCard>
  </>
);
